import { Author, CodedError, QuizSession } from '..';
import { makeRequest, CommonRequestOptions } from '../makeRequest';
import { CourseAssignmentCompletionDocument } from '../course-assignments';
import { createErrorMessageTypeGuard } from '../createErrorMessageTypeGuard';
import { QuizAssessment } from '~/pages/Courses/_shared/CourseForm/CourseStageEditor/Stage';

export type UnsavedCourse = {
   title: string;
   contents: string;
   stages: UnsavedStage[];
   enabled?: boolean;
   publish_date?: number;
};

export type UserAssignee = {
   userid: number;
   username: string;
   course_assignmentid: number;
   image: string;
   completionTotal: number;
};

export type TeamMember = Omit<UserAssignee, 'course_assignmentid'>;

export type TeamAssignee = {
   teamid: number;
   name: string;
   course_assignmentid: number;
   members: Array<TeamMember>;
   completionTotal: number;
};

export type Assignee = UserAssignee | TeamAssignee;

export function isTeamAssignee(assignee: Assignee): assignee is TeamAssignee {
   if ('teamid' in assignee) {
      return true;
   }

   return false;
}

export type DataCaptureEntry = {
   entryid: number;
   guideid: number;
   userid: number;
   workorderid: string | null;
   starttime: number;
   endtime: number | null;
   max_form_revisionid: number | null;
   cancelled: boolean | null;
   guide_revisionid: number | null;
   handoffid: number | null;
   langid: string | null;
};

export type Course = Omit<UnsavedCourse, 'stages'> & {
   wikiid: number;
   assigneeCount?: number;
   // The API returns this field as 'assignee_count', but our components expect 'assigneeCount'.
   // See type definition in 'monolith-api/courses.ts' for how both fields are represented.
   // We include both here to maintain backward compatibility and prevent breaking changes.
   assignee_count?: number;
   contents_raw: string;
   created_date: number;
   modified_date: number;
   stages: Stage[];
   assignees?: Array<Assignee>;
   author: Author;
   completion_percentage: number;
   stages_count: number;
   status: string;
   fullUrl: string;
};

export type StageDocSummaryInfo = {
   dataCaptureUrl: string;
   guideViewUrl: string;
   viewUrl: string;
   isHidden: boolean;
   requiresSignoff: boolean;
   stepCount: number;
   title: string;
   upcomingRelease: Object | null;
   version: string | null;
   viewUserEntryPdfUrl: string | null;
   attachedQuiz?: QuizAssessment;
   workLogEnabled: boolean;
   public: boolean;
   archived?: boolean;
   releaseTitle?: string | null;
   standardImage?: string;
   learningStandard?: string;
   quizSession?: QuizSession;
   releaseDate?: number;
   dataCaptureEntry?: DataCaptureEntry | null;
};

export type UnsavedStageDocSummaryInfo = Pick<
   StageDocSummaryInfo,
   'releaseTitle' | 'standardImage' | 'title' | 'public' | 'learningStandard' | 'attachedQuiz'
>;

type SignoffTeam = {
   teamid: number;
   name: string;
   image: string;
};

export type Assessment = {
   stage_assessmentid: number;
   is_completed: boolean;
   assessmentid: null;
   assessmenttype: 'trainer-sign-off';
   skills_points_possible: number;
   teamid_signer?: number;
   signoffTeam?: SignoffTeam | null;
};

export type UnsavedAssessment = Omit<Assessment, 'stage_assessmentid' | 'is_completed'>;

export type UnsavedStage = CourseAssignmentCompletionDocument & {
   frequency_unit: StageFrequency;
   use_datacapture: boolean | null;
   frequency_quantity: number | null;
   strictness: StageStrictness;
   completion?: number | null;
   docSummaryInfo?: UnsavedStageDocSummaryInfo;
   assessments?: UnsavedAssessment[];
};

export type Stage = Omit<UnsavedStage, 'docSummaryInfo' | 'assessments'> & {
   doc_url: string;
   course_stageid: number;
   title: string;
   docSummaryInfo?: StageDocSummaryInfo;
   assessments: Assessment[];
   quiz_attempts_remaining?: number;
   orderby: number;
};

export type StageFrequency = null | 'day' | 'week' | 'month' | 'year';

export type StageStrictness = null | 'major' | 'minor';

export async function getCourse(
   wikiid: number | null,
   common: CommonRequestOptions = {}
): Promise<Course | null> {
   if (wikiid === null) return null;
   return makeRequest('get', `courses/${wikiid}`, common);
}

type GetCourseErrorCode = 'courses.course_not_found';
getCourse.errorMessages = createErrorMessageTypeGuard<GetCourseErrorCode>();

export async function createCourse(
   course: UnsavedCourse,
   common: CommonRequestOptions = {}
): Promise<Course> {
   return await makeRequest('post', 'courses', {
      ...common,
      body: withModifiedStages(course),
   });
}

export type CreateCourseErrorCode =
   | 'courses.create.duplicate_title'
   | 'courses.missing_teamid_signer';
createCourse.errorMessages = createErrorMessageTypeGuard<CreateCourseErrorCode>();

export async function editCourse(
   wikiid: number,
   editedCourse: UnsavedCourse,
   common: CommonRequestOptions = {}
): Promise<Course> {
   return await makeRequest('patch', `courses/${wikiid}`, {
      ...common,
      body: withModifiedStages(editedCourse),
   });
}

function withModifiedStages(course: UnsavedCourse): UnsavedCourse {
   const stageWithInvalidAssessment = course.stages.some(stage => {
      const assessment = stage.assessments?.[0];
      if (!assessment) return false;
      return assessment.assessmenttype === 'trainer-sign-off' && !assessment.teamid_signer;
   });

   if (stageWithInvalidAssessment) {
      // Throw an error so that the mutation does not run onSuccess callbacks
      throw new CodedError(
         'Tried to save Stage Sign-Off without a teamid_signer',
         'courses.missing_teamid_signer'
      );
   }

   return {
      title: course.title,
      contents: course.contents,
      enabled: course.enabled,
      // @ts-ignore
      stages: course.stages.map(stage => ({
         docid: stage.docid,
         doctype: stage.doctype,
         strictness: stage.strictness,
         frequency_unit: stage.frequency_unit,
         use_datacapture: stage.use_datacapture,
         frequency_quantity: stage.frequency_quantity,
         assessments: stage.assessments?.map(assessment => ({
            ...assessment,
            /** We need to update the course service to accept null as a valid assessmentid,
               but this will be workable in the meantime  */
            assessmentid: assessment.assessmentid ?? 0,
            assessmenttype: assessment.assessmenttype,
            teamid_signer:
               assessment.assessmenttype === 'trainer-sign-off'
                  ? assessment.teamid_signer ?? 1
                  : undefined,
         })),
      })),
   };
}

export type EditCourseErrorCode =
   | 'courses.course_not_found'
   | 'courses.edit.duplicate_title'
   | 'courses.missing_teamid_signer';
editCourse.errorMessages = createErrorMessageTypeGuard<EditCourseErrorCode>();

export type CourseOrderbyField =
   | 'title'
   | 'status'
   | 'stages_count'
   | 'modified_date'
   | 'assignee_count'
   | 'completion_percentage';
export type GetCoursesSortOption = 'DESC' | 'ASC';

export enum CourseStatus {
   ENABLED = 'enabled',
   DISABLED = 'disabled',
}

export type GetCoursesRequestOptions = CommonRequestOptions & {
   orderby?: CourseOrderbyField;
   direction?: 'DESC' | 'ASC';
   disabledOkay?: boolean;
   page?: number;
   pageSize?: number;
   query?: string;
   userid?: number | null;
   status?: CourseStatus[];
};

export type GetCoursesResult = {
   courses: Course[];
   hasMore: boolean;
   total: number;
};

export async function getCourses({
   orderby,
   direction,
   disabledOkay,
   page,
   pageSize,
   query,
   userid,
   status,
   ...common
}: GetCoursesRequestOptions = {}): Promise<GetCoursesResult> {
   const statusParam = status?.map(s => s.toLowerCase()).join(',');
   return makeRequest('get', 'courses', {
      ...common,
      query: {
         orderby,
         direction,
         disabledOkay,
         page,
         pageSize,
         query,
         userid,
         status: statusParam,
      },
   });
}
